import {
    createEntityAdapter,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit';

import {generateRandomString} from 'shared/utils/random';
import {TAppState} from 'store/config/types';

import {API_REQUEST_PROGRESS_SLICE} from './api-request-progress-slice-constants';
import {IApiRequestProgressEntity, TApiRequestProgressSliceState} from './api-request-progress-slice-types';

const requestsProgressEntityAdapter = createEntityAdapter<IApiRequestProgressEntity>({
    selectId: model => model.id,
});

export const apiRequestProgressSlice = createSlice({
    initialState: {
        requestsProgress: requestsProgressEntityAdapter.getInitialState(),
        someData: 'data',
    } as TApiRequestProgressSliceState,
    name: API_REQUEST_PROGRESS_SLICE,
    reducers: {
        purgeRequestProgress(state) {
            requestsProgressEntityAdapter.removeAll(state.requestsProgress);
        },
        setRequestProgress(state, {
            payload,
        }: PayloadAction<IApiRequestProgressEntity[]>) {
            requestsProgressEntityAdapter.setAll(state.requestsProgress, {
                ...payload,
            });
        },
        addRequestProgress(state, {
            payload,
        }: PayloadAction<Omit<IApiRequestProgressEntity, 'id'> & {
            id?: string;
        }>) {
            const nextId = payload.id ?? generateRandomString();

            requestsProgressEntityAdapter.upsertOne(state.requestsProgress, {
                id: nextId,
                ...payload,
            });
        },
        updateRequestProgressPercent(state, {payload}: PayloadAction<{
            id: IApiRequestProgressEntity['id'];
            progressPercent: number;
        }>) {
            const {id, progressPercent} = payload;
            if (state.requestsProgress.ids.includes(id)) {
                requestsProgressEntityAdapter.updateOne(state.requestsProgress, {
                    changes: {
                        progressPercent,
                    },
                    id,
                });
            }
        },
        removeRequestProgress(state, {payload}: PayloadAction<IApiRequestProgressEntity['id']>) {
            requestsProgressEntityAdapter.removeOne(state.requestsProgress, payload);
        },
    },
});

const selectApiRequestProgressSliceState = (state: TAppState) => state[API_REQUEST_PROGRESS_SLICE];

const requestsProgressEntityAdapterSelectors = requestsProgressEntityAdapter.getSelectors(
    (state: TAppState) => selectApiRequestProgressSliceState(state).requestsProgress,
);

export const apiRequestProgressSelectors = {
    selectApiRequestProgressSliceState,
    ...requestsProgressEntityAdapterSelectors,
};

export const {
    reducer: apiRequestProgressSliceReducer,
    actions: apiRequestProgressSliceActions,
} = apiRequestProgressSlice;
